import { Component, ContentChild, forwardRef, Input, OnInit, TemplateRef } from '@angular/core';
import {
    ControlContainer,
    ControlValueAccessor,
    FormControl,
    FormGroup,
    NG_VALUE_ACCESSOR,
    ValidationErrors,
} from '@angular/forms';

@Component({
    selector: 'app-input[type=numeric]',
    templateUrl: './input-numeric.component.html',
    styleUrls: ['./input-numeric.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => InputNumericComponent),
            multi: true,
        },
    ],
})
export class InputNumericComponent implements ControlValueAccessor, OnInit {
    @Input() formControlName: string;
    @Input() formControl: FormControl;
    @Input() trimLeadingZeros: boolean;
    @Input() disallowLeadingZero: boolean;
    @Input() allowDecimal: boolean = true;
    @Input() identifier: string;
    @Input() placeholder: string;
    @Input() label: string;
    @Input() maxLength: number;
    @Input() suffix: string;
    @Input() hint: string;

    @ContentChild('suffix') suffixTmpl: TemplateRef<any>;

    private _value: any;
    private _isDisabled: boolean;
    private _onBaseChange: () => void;
    private _onBaseTouched: () => void;

    constructor(private controlContainer: ControlContainer) {}

    public handleInput(event: Event): void {
        const inputElement = event.target as HTMLInputElement;
        const value = inputElement.value || '';

        let numeric = this.allowDecimal ? value.replace(/[^0-9.]/g, '') : value.replace(/\D/g, '');

        if (this.allowDecimal) {
            const parts = numeric.split('.');
            if (parts.length > 2) {
                numeric = parts[0] + '.' + parts.slice(1).join('');
            }
        }

        if (
            this.disallowLeadingZero &&
            numeric.startsWith('0') &&
            numeric.length > 1 &&
            numeric[1] !== '.'
        ) {
            numeric = numeric.replace(/^0+/, '');
        }

        let finalValue: string | number = numeric;
        if (this.trimLeadingZeros && numeric !== '' && !numeric.includes('.')) {
            finalValue = +numeric;
        }

        this.formControl.patchValue(finalValue, { emitEvent: false });
    }

    ngOnInit(): void {
        this.setBaseStateInitialization();
    }

    private setBaseStateInitialization(): void {
        if (!this.formControlName) return;
        const formGroup = this.controlContainer.control as FormGroup;
        this.formControl = formGroup.get(this.formControlName) as FormControl;
    }

    public get isValid(): boolean {
        return !!(this.formControl?.touched && this.formControl?.valid);
    }

    public get isInvalid(): boolean {
        return !!(this.formControl?.touched && this.formControl?.invalid);
    }

    public get errors(): ValidationErrors | null {
        return this.formControl.errors;
    }

    writeValue(value: any): void {
        this._value = value;
    }

    registerOnChange(fn: any): void {
        this._onBaseChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onBaseTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        this._isDisabled = isDisabled;
    }
}
